import React from 'react'
import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonTitle,
  IonToolbar,
} from '@ionic/react'
import { chevronBack, informationCircle } from 'ionicons/icons'
import PCRHeader from '../../../components/pcrHeader/PCRHeader.component'
import SegmentedSelect, {
  ISegmentData,
  ISegmentOption,
  ISegmentPNNVOption,
} from '../../../components/segmentedSelect/SegmentedSelect.component'
import { useHistory } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { getDiagnosisOptionsSelector } from '../../options/selectors/getDiagnosisOptions.selector'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { showAlert } from '../../../components/alert/store/alert.actions'
import { setImpressions, setImpressionsPNNV } from '../store/diagnosis.actions'
import { getDiagnosisSelector } from '../selectors/getDiagnosis.selector'
import { DiagnosisConstants } from '../store/diagnosis.constants'

const Diagnosis: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()

  const diagnosisOptions = useSelector(getDiagnosisOptionsSelector)
  const { impressionsOptions } = diagnosisOptions

  const diagnosisState = useSelector(getDiagnosisSelector)
  const { impressions, impressionsPNNV } = diagnosisState

  const onBackClick = () => {
    save()
  }

  const isSaveValid = () => {
    const primaryValues = impressions.filter((value) => value.segmentId === DiagnosisConstants.PRIMARY_SEGMENT_ID)
    const secondaryValues = impressions.filter((value) => value.segmentId === DiagnosisConstants.SECONDARY_SEGMENT_ID)
    const otherValues = impressions.filter((value) => value.segmentId === DiagnosisConstants.OTHER_SEGMENT_ID)

    if ((secondaryValues.length > 0 || otherValues.length > 0) && primaryValues.length < 1) {
      dispatch(
        showAlert({
          message: t('diagnosis.primaryNotPresentError'),
        })
      )
      return false
    }
    return true
  }

  const save = () => {
    if (isSaveValid()) history.push(TabConstants.CC_HPI)
  }

  const clearSelectedValues = () => {
    dispatch(setImpressions([]))
    dispatch(setImpressionsPNNV(null))
  }

  const onchange = (values: ISegmentOption[]) => {
    dispatch(setImpressions(values))
  }

  const onPNNVChange = (value: ISegmentPNNVOption | null) => {
    if (value?.segmentId === +DiagnosisConstants.PRIMARY_SEGMENT_ID && impressions && impressions.length > 0)
      dispatch(setImpressions([]))
    if (value?.segmentId === +DiagnosisConstants.SECONDARY_SEGMENT_ID) {
      const impressionsObj = impressions.filter(
        (obj) =>
          obj.segmentId !== +DiagnosisConstants.SECONDARY_SEGMENT_ID &&
          obj.segmentId !== +DiagnosisConstants.OTHER_SEGMENT_ID
      )
      dispatch(setImpressions(impressionsObj))
    }
    dispatch(setImpressionsPNNV(value))
  }

  const pnnvSelectedInPrimaryTab =
    !!impressionsPNNV && impressionsPNNV.segmentId === +DiagnosisConstants.PRIMARY_SEGMENT_ID

  const pnnvSelectedInPrimaryOrSecondaryTab =
    !!impressionsPNNV &&
    (impressionsPNNV.segmentId === +DiagnosisConstants.PRIMARY_SEGMENT_ID ||
      impressionsPNNV.segmentId === +DiagnosisConstants.SECONDARY_SEGMENT_ID)

  const segmentsMetdaData: ISegmentData[] = [
    {
      id: +DiagnosisConstants.PRIMARY_SEGMENT_ID,
      name: t('diagnosis.primary'),
      type: 'single',
      color: '#7145A9',
      disabled: false,
    },
    {
      id: +DiagnosisConstants.SECONDARY_SEGMENT_ID,
      name: t('diagnosis.secondary'),
      type: 'single',
      color: '#99595E',
      disabled: pnnvSelectedInPrimaryTab,
    },
    {
      id: +DiagnosisConstants.OTHER_SEGMENT_ID,
      name: t('diagnosis.other'),
      type: 'multi',
      color: '#599199',
      disabled: pnnvSelectedInPrimaryOrSecondaryTab,
    },
  ]

  const infoPopupContent = () => {
    return (
      <IonList>
        <IonItem lines='none'>
          <IonIcon className='info-icon-margin' slot='start' icon={informationCircle}></IonIcon>
          <IonLabel>
            <h1>
              {t('diagnosis.symptoms')} {t('diagnosis.types')}
            </h1>
          </IonLabel>
        </IonItem>

        <IonItem lines='none'>
          <IonLabel>
            <h3>{t('diagnosis.primary')}</h3>
            <p style={{ whiteSpace: 'break-spaces' }}>{t('diagnosis.primaryImpressionDescr')}</p>
          </IonLabel>
        </IonItem>

        <hr />

        <IonItem lines='none'>
          <IonLabel>
            <h3>{t('diagnosis.secondary')}</h3>
            <p style={{ whiteSpace: 'break-spaces' }}>{t('diagnosis.secondaryImpressionDescr')}</p>
          </IonLabel>
        </IonItem>

        <hr />

        <IonItem lines='none'>
          <IonLabel>
            <h3>{t('diagnosis.other')}</h3>
            <p style={{ whiteSpace: 'break-spaces' }}>{t('diagnosis.otherImpressionDescr')}</p>
          </IonLabel>
        </IonItem>
      </IonList>
    )
  }

  return (
    <IonPage>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' color='primary' onClick={onBackClick}>
              <IonIcon icon={chevronBack} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonTitle data-testid='HeaderTitle'>{t('diagnosis.impression')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent color='light' fullscreen>
        <SegmentedSelect
          options={impressionsOptions}
          segmentsData={segmentsMetdaData}
          values={[...impressions]}
          pnnvValue={impressionsPNNV}
          infoPopupContent={infoPopupContent()}
          onChange={onchange}
          onPNNVChange={onPNNVChange}
        />
      </IonContent>
      <IonFooter className='ion-no-border'>
        <IonToolbar color='light' className='p-10'>
          <IonButtons>
            <IonButton
              data-testid='ClearAll'
              className='btn mr-8'
              color='danger'
              fill='outline'
              expand='block'
              onClick={() => clearSelectedValues()}>
              {t('common.buttons.ClearAll')}
            </IonButton>
            <IonButton
              data-testid='MultiSelectSave'
              className='btn btn-primary'
              expand='block'
              onClick={() => save()}
              id='multiselect-modal-save-button'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  )
}

export default Diagnosis
